'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface QuoteRequestFormProps {
  mechanicName: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: string;
  serviceType: string;
  description: string;
  preferredContact: string;
}

const QuoteRequestForm = ({ mechanicName }: QuoteRequestFormProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    serviceType: '',
    description: '',
    preferredContact: 'phone'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-xl shadow-elevation-md p-6">
        <div className="h-6 bg-muted rounded w-40 mb-6" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-12 bg-muted rounded" />
          ))}
        </div>
      </div>
    );
  }

  const vehicleMakes = ['BMW', 'Lexus', 'Mercedes-Benz', 'Audi', 'Acura', 'Infiniti', 'Toyota', 'Honda'];
  const serviceTypes = [
    'Oil Change',
    'Brake Service',
    'Engine Diagnostics',
    'Transmission Repair',
    'Suspension Work',
    'Electrical Systems',
    'AC Repair',
    'Body Shop',
    'General Maintenance',
    'Other'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    setTimeout(() => {
      setSubmitSuccess(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        vehicleMake: '',
        vehicleModel: '',
        vehicleYear: '',
        serviceType: '',
        description: '',
        preferredContact: 'phone'
      });
    }, 3000);
  };

  if (submitSuccess) {
    return (
      <div className="bg-card rounded-xl shadow-elevation-md p-6">
        <div className="text-center py-12">
          <div className="flex justify-center mb-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
              <Icon name="CheckCircleIcon" size={32} className="text-success" variant="solid" />
            </div>
          </div>
          <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">
            Quote Request Sent!
          </h3>
          <p className="font-body text-sm text-text-secondary max-w-md mx-auto">
            {mechanicName} will contact you within 2 hours during business hours. Check your {formData.preferredContact} for their response.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-elevation-md p-6">
      <div className="mb-6">
        <h2 className="font-heading text-xl font-semibold text-text-primary mb-2">
          Request a Quote
        </h2>
        <p className="font-body text-sm text-text-secondary">
          Get a detailed estimate from {mechanicName} for your repair needs
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-border bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label htmlFor="email" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-border bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              placeholder="john@example.com"
            />
          </div>
        </div>

        <div>
          <label htmlFor="phone" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className="w-full rounded-lg border border-border bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            placeholder="(555) 123-4567"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label htmlFor="vehicleMake" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
              Vehicle Make *
            </label>
            <select
              id="vehicleMake"
              name="vehicleMake"
              value={formData.vehicleMake}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-border bg-input px-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <option value="">Select Make</option>
              {vehicleMakes.map(make => (
                <option key={make} value={make}>{make}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="vehicleModel" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
              Model *
            </label>
            <input
              type="text"
              id="vehicleModel"
              name="vehicleModel"
              value={formData.vehicleModel}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-border bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              placeholder="3 Series"
            />
          </div>

          <div>
            <label htmlFor="vehicleYear" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
              Year *
            </label>
            <input
              type="text"
              id="vehicleYear"
              name="vehicleYear"
              value={formData.vehicleYear}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-border bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              placeholder="2020"
            />
          </div>
        </div>

        <div>
          <label htmlFor="serviceType" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
            Service Type *
          </label>
          <select
            id="serviceType"
            name="serviceType"
            value={formData.serviceType}
            onChange={handleChange}
            required
            className="w-full rounded-lg border border-border bg-input px-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <option value="">Select Service</option>
            {serviceTypes.map(service => (
              <option key={service} value={service}>{service}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="description" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
            Service Description *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows={4}
            className="w-full rounded-lg border border-border bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 resize-none"
            placeholder="Describe the issue or service you need..."
          />
        </div>

        <div>
          <label className="font-caption text-xs font-medium text-text-secondary mb-2 block">
            Preferred Contact Method *
          </label>
          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="preferredContact"
                value="phone"
                checked={formData.preferredContact === 'phone'}
                onChange={handleChange}
                className="h-4 w-4 border-border text-primary transition-smooth focus:ring-2 focus:ring-primary focus:ring-offset-2"
              />
              <span className="font-body text-sm text-text-primary">Phone</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="preferredContact"
                value="email"
                checked={formData.preferredContact === 'email'}
                onChange={handleChange}
                className="h-4 w-4 border-border text-primary transition-smooth focus:ring-2 focus:ring-primary focus:ring-offset-2"
              />
              <span className="font-body text-sm text-text-primary">Email</span>
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center space-x-2 rounded-lg bg-primary px-6 py-4 font-body text-base font-medium text-primary-foreground shadow-elevation-sm transition-smooth hover:opacity-90 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              <span>Sending Request...</span>
            </>
          ) : (
            <>
              <Icon name="PaperAirplaneIcon" size={20} />
              <span>Send Quote Request</span>
            </>
          )}
        </button>

        <div className="rounded-lg bg-muted p-4">
          <div className="flex items-start space-x-3">
            <Icon name="InformationCircleIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-body text-xs text-text-secondary leading-relaxed">
                By submitting this form, you agree to be contacted by {mechanicName} regarding your quote request. Your information will not be shared with third parties.
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default QuoteRequestForm;